import type { ReactNode } from 'react'
import styles from './OpportunitiesListEmpty.module.css'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Button } from '@/components/Button'
import { byDesign } from '@/infra/design'
import imgSearchNoresultsUrl from '@/assets/misc/img_search-noresults.svg'
import SearchNoResultsIllustration from '@/components/SpotIllustration/SearchNoResultsIllustration'
import type { FittedIllustration } from '@/components/SpotIllustration/fitted-illustration'

interface OpportunitiesListEmptyProps {
  /**
   * Empty state title.
   */
  title?: string
  /**
   * Empty state subtitle.
   */
  subtitle?: ReactNode
  /**
   * Illustration image URL.
   */
  imageSrc?: string
  /**
   * Illustration shown instead of `imageSrc` when set.
   */
  illustration?: FittedIllustration
  /**
   * Callback for clearing the search filter.
   */
  onClearSearchFilter?: () => unknown
  /**
   * Children.
   */
  children?: ReactNode
  /**
   * Whether the workspace wants a corporate look, which hides illustrations.
   */
  corporateMode: boolean
}

export function OpportunitiesListEmpty({
  subtitle,
  onClearSearchFilter,
  title = 'We looked everywhere...',
  imageSrc,
  illustration,
  children,
  corporateMode,
}: OpportunitiesListEmptyProps) {
  subtitle = subtitle || (
    <>
      But we couldn't find what you were looking for.
      {onClearSearchFilter && (
        <>
          <br />
          Maybe try{' '}
          <Button
            label="clearing your search filter"
            size="baseline"
            onPress={onClearSearchFilter}
            variant="text"
          />{' '}
          ?
        </>
      )}
    </>
  )
  return (
    <div className={styles.root}>
      <EmptyState size="sm">
        {!corporateMode && (
          <EmptyStateIllustration
            src={imageSrc ?? imgSearchNoresultsUrl}
            illustration={
              illustration ??
              // A caller's own image replaces the default in either design.
              (imageSrc
                ? undefined
                : byDesign(undefined, SearchNoResultsIllustration))
            }
          />
        )}
        <EmptyStateTitle>{title}</EmptyStateTitle>
        <EmptyStateDescription>{subtitle}</EmptyStateDescription>
        {children && <EmptyStateActions>{children}</EmptyStateActions>}
      </EmptyState>
    </div>
  )
}
